/**
 * WebSocket-backed live-event subscription bus.
 *
 * One process-wide WebSocket connects to `/live`; subscribers register
 * via `subscribe(handler)` and receive every `LiveEvent` from any tab
 * scoped to the user's current company. The connection auto-reconnects
 * with exponential backoff (1s → 2s → 4s → 8s → 16s, capped at 30s)
 * so a brief network drop doesn't drop subscribers' coverage.
 *
 * Pre-extraction this lived in `api.ts` mixed in with HTTP resource
 * methods; moving it out leaves api.ts as a cleaner per-resource
 * registry, and a future centralized live-sync hook (CLAUDE.md
 * follow-up) imports straight from here without dragging the full
 * api object into its dependency graph.
 *
 * Notes for future readers:
 *   - There is intentionally NO event-type filtering here. Filtering
 *     happens at the SUBSCRIBER (e.g., TicketDetail uses
 *     `isLiveTicketEvent(e, ticketId)` to scope to its ticket).
 *   - The bus is global-singleton per-page, not per-route. Mounting a
 *     new component that calls `subscribe` returns the same WS.
 *     Unmounting removes the listener, NOT the WS itself.
 *   - `wsReconnectAttempt` resets to 0 once a socket delivers a message,
 *     so long-lived connections that briefly blip don't carry over the
 *     attempt count, and a socket the server refuses keeps backing off.
 */

import type { LiveEvent } from '@vd/types';
import { API_BASE } from './apiClient';
import { pageAccount } from './pageAccount';

const liveListeners = new Set<(e: LiveEvent) => void>();
let ws: WebSocket | null = null;
let wsReconnectTimer: number | undefined;
let wsReconnectAttempt = 0;

function scheduleReconnect() {
  if (wsReconnectTimer) window.clearTimeout(wsReconnectTimer);
  // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s.
  const delay = Math.min(30_000, 1000 * 2 ** wsReconnectAttempt);
  wsReconnectAttempt = Math.min(wsReconnectAttempt + 1, 6);
  wsReconnectTimer = window.setTimeout(ensureWebSocket, delay);
}

function ensureWebSocket() {
  if (typeof window === 'undefined') return;
  if (ws && ws.readyState <= 1) return;
  // Honor VITE_API_BASE when set so cross-origin deployments (api on
  // a separate subdomain) point the WebSocket at the right host. The
  // ws/wss scheme is derived from the API base when supplied,
  // otherwise from window.location.
  let url: string;
  if (API_BASE) {
    url = API_BASE.replace(/^http(s?):/, 'ws$1:') + '/live';
  } else {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    url = `${proto}://${window.location.host}/live`;
  }
  // A socket cannot carry X-Toado-Account, so the page names its account in
  // the address: a reconnect under another tab's sign-in is not let in to
  // hear that account's events (`liveSocketUser` on the API).
  const account = pageAccount();
  if (account) url += `?account=${encodeURIComponent(account)}`;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }
  ws.addEventListener('message', (msg) => {
    // Backoff resets once the socket has delivered something (the server's
    // keepalive at the latest), not when it opens: a socket the server refuses
    // opens first and then closes, and resetting there reconnected every
    // second for as long as the refusal lasted.
    wsReconnectAttempt = 0;
    try {
      const data = JSON.parse(msg.data);
      const event: LiveEvent = data.event ?? data;
      for (const l of liveListeners) l(event);
    } catch {
      /* ignore */
    }
  });
  ws.addEventListener('close', (event) => {
    ws = null;
    // 4001: the server would not let the socket in, its session ended or
    // another account's now. The page finds out which (AuthProvider), as for
    // any request answered 401 (review of 0.2.0, 3 Oct).
    if (event.code === 4001) window.dispatchEvent(new CustomEvent('vd:unauthorized'));
    scheduleReconnect();
  });
  ws.addEventListener('error', () => {
    // close handler will reconnect with backoff
  });
}

/**
 * Register a handler for live events. Returns an unsubscribe function;
 * call it from a useEffect cleanup to keep listeners scoped to the
 * mounted component. The WebSocket is shared across all subscribers
 * — this just adds/removes listeners on the local Set.
 */
export function subscribe(handler: (e: LiveEvent) => void): () => void {
  liveListeners.add(handler);
  ensureWebSocket();
  return () => {
    liveListeners.delete(handler);
  };
}
